// „Auftakt" (Takt 1 als Pickup markieren bzw. aufheben). Eigenes Modul, weil es mehr ist als ein
// Struktur-Edit an einer Stelle: führende Pausen ALLER Parts/Stimmen kürzen, `<backup>` nachziehen,
// den Schlusstakt ergänzen und neu nummerieren. Die Stimmen-Abschnitte („Runs") samt Kürzen/Auffüllen
// liegen in `measureRuns.ts` (geteilt mit `meterChange.ts`). Abhängigkeitsregel wie im ganzen Ordner:
// Basis-Helfer aus `core.ts`, gerichtete Geschwister-Imports (structure, measureAttributes,
// measureRuns), keine Zyklen.
import {getPartDivisions, getParts, parseMusicXml, serializeMusicXml} from './core';
import {expectedMeasureTicks} from './measureAttributes';
import {type Run, leadingRestTicks, padRun, splitRuns, trailingRestTicks, trimRun} from './measureRuns';
import {applyMeasureAttrs, renumberMeasures} from './structure';

/** Zustand des ersten Takts für den Schalter im Takt-Editor „Takte". */
export interface PickupState {
    /** Takt 1 ist als Auftakt markiert (`implicit="yes"` oder `number="0"`). */
    isPickup: boolean;
    /** Der Schalter darf umgelegt werden; sonst steht der Grund in `reason`. */
    canToggle: boolean;
    reason: string;
}

export interface SetPickupResult {
    xml: string;
    /** Rückmeldung für den Nutzer, inkl. Hinweis, wenn der Schlusstakt nicht angepasst werden konnte. */
    message: string;
}

/** Ein Takt eines Parts samt geltendem Metrum. */
interface MeasureScan {
    measure: Element;
    runs: Run[];
    divisions: number;
    expected: number;
    content: number;
}

function scanMeasure(part: Element, index: number): MeasureScan | null {
    const measures = [...part.querySelectorAll('measure')];
    const measure = measures[index];
    if (!measure) return null;
    const runs = splitRuns(measure);
    if (!runs) return null;
    const state = new Map<string, Element>();
    for (let i = 0; i <= index; i++) applyMeasureAttrs(measures[i]!, state);
    const divisions = parseInt(state.get('divisions')?.textContent || '', 10) || getPartDivisions(part) || 1;
    const time = state.get('time');
    const beats = parseInt(time?.querySelector('beats')?.textContent || '4', 10) || 4;
    const beatType = parseInt(time?.querySelector('beat-type')?.textContent || '4', 10) || 4;
    return {
        measure, runs, divisions,
        expected: expectedMeasureTicks(beats, beatType, divisions),
        content: Math.max(0, ...runs.map(r => r.length)),
    };
}

interface PickupPlan {
    parts: Element[];
    firsts: MeasureScan[];
    lasts: MeasureScan[] | null;
    isPickup: boolean;
    /** Ticks, die beim Markieren vorne abgeschnitten werden (0 = schon kurz, nur markieren). */
    cut: number;
    reason: string;
}

const STRUCTURE_REASON = 'Takt 1 hat eine Stimmenstruktur, die sich nicht automatisch kürzen lässt';

function planPickup(doc: Document): PickupPlan | null {
    const parts = getParts(doc);
    if (!parts.length) return null;
    const firstMeasure = parts[0]!.querySelector('measure');
    if (!firstMeasure) return null;
    const isPickup = firstMeasure.getAttribute('implicit') === 'yes' || firstMeasure.getAttribute('number') === '0';
    const measureCount = parts[0]!.querySelectorAll('measure').length;

    const firsts = parts.map(p => scanMeasure(p, 0));
    const lastsRaw = measureCount > 1 ? parts.map(p => scanMeasure(p, measureCount - 1)) : null;
    const lasts = lastsRaw && lastsRaw.every(s => s) ? lastsRaw as MeasureScan[] : null;
    if (firsts.some(s => !s)) {
        return {parts, firsts: [], lasts, isPickup, cut: 0, reason: STRUCTURE_REASON};
    }
    const scans = firsts as MeasureScan[];
    const plan: PickupPlan = {parts, firsts: scans, lasts, isPickup, cut: 0, reason: ''};
    if (isPickup) return plan;

    const content = Math.max(...scans.map(s => s.content));
    const expected = scans[0]!.expected;
    if (content <= 0) return {...plan, reason: 'Takt 1 ist leer'};
    if (content < expected) return plan;   // schon kurz → nur markieren
    const cut = Math.min(...scans.flatMap(s => s.runs.map(leadingRestTicks)));
    if (cut <= 0) return {...plan, reason: 'Takt 1 beginnt in einer Stimme auf Schlag 1 – keine führenden Pausen zum Abschneiden'};
    if (cut >= content) return {...plan, reason: 'Takt 1 enthält nur Pausen'};
    return {...plan, cut};
}

/** Liest, ob Takt 1 ein Auftakt ist und ob sich das umschalten lässt – rein lesend. */
export function pickupState(musicXml: string): PickupState {
    try {
        const plan = planPickup(parseMusicXml(musicXml));
        if (!plan) return {isPickup: false, canToggle: false, reason: 'Keine Takte gefunden'};
        return {isPickup: plan.isPickup, canToggle: !plan.reason, reason: plan.reason};
    } catch {
        return {isPickup: false, canToggle: false, reason: 'MusicXML nicht lesbar'};
    }
}

/**
 * Markiert Takt 1 als **Auftakt** (`enabled = true`) bzw. hebt das auf.
 *
 * - **Markieren:** schneidet vorne die Pausenzeit ab, die in **allen** Parts/Stimmen/Staffs vor dem
 *   ersten Ton liegt – keine Note geht verloren. Ist Takt 1 schon kürzer als das Metrum, wird er nur
 *   markiert. Danach `implicit="yes"`, Nummer 0, Folgetakte ab 1 (`renumberMeasures`). music21 erkennt
 *   einen Auftakt nur am Inhalt (Takt kürzer als das Metrum → `paddingLeft`), `implicit` allein
 *   reicht ihm nicht – deshalb das Kürzen.
 * - **Schlusstakt:** endet der letzte Takt in allen Stimmen mit mindestens `Metrum − Auftaktlänge`
 *   Pausen, wird genau so viel abgeschnitten (Auftakt + Schlusstakt = ein Takt). Sonst bleibt er,
 *   die `message` sagt es.
 * - **Aufheben:** füllt Takt 1 vorne und einen kurzen Schlusstakt hinten mit Pausen auf Metrumlänge,
 *   entfernt `implicit`, Nummern ab 1.
 *
 * `<harmony>`/`<direction>` bleiben an ihrer Stelle im Dokument und rücken mit den Noten. `null`, wenn
 * nichts zu tun ist oder der Takt sich nicht umschalten lässt (Grund: {@link pickupState}).
 */
export function setPickupInMusicXml(musicXml: string, enabled: boolean): SetPickupResult | null {
    try {
        const doc = parseMusicXml(musicXml);
        const plan = planPickup(doc);
        if (!plan || plan.reason || plan.isPickup === enabled) return null;
        const message = enabled ? markPickup(doc, plan) : unmarkPickup(doc, plan);
        return {xml: serializeMusicXml(doc), message};
    } catch (e) {
        console.error('Error setting pickup in MusicXML:', e);
        return null;
    }
}

function markPickup(doc: Document, plan: PickupPlan): string {
    const content = Math.max(...plan.firsts.map(s => s.content));
    for (const scan of plan.firsts) {
        for (const run of scan.runs) {
            if (plan.cut > 0) trimRun(doc, run, plan.cut, scan.divisions, false);
        }
    }

    let note = '';
    const pickupTicks = content - plan.cut;
    if (plan.lasts) {
        const lastContent = Math.max(...plan.lasts.map(s => s.content));
        const complement = plan.lasts[0]!.expected - pickupTicks;
        const lastIsFull = lastContent >= plan.lasts[0]!.expected;
        const fits = plan.lasts.every(s => s.runs.every(r => (s.content - r.length) + trailingRestTicks(r) >= complement));
        if (complement > 0 && lastIsFull) {
            if (fits) {
                for (const scan of plan.lasts) {
                    for (const run of scan.runs) {
                        const own = complement - (scan.content - run.length);
                        if (own > 0) trimRun(doc, run, own, scan.divisions, true);
                    }
                }
                note = ', Schlusstakt angepasst';
            } else {
                note = ' – Schlusstakt nicht angepasst (endet nicht in allen Stimmen mit passenden Pausen)';
            }
        }
    }

    for (const part of plan.parts) {
        part.querySelector('measure')?.setAttribute('implicit', 'yes');
        renumberMeasures(part);
    }
    return `Auftakt gesetzt${note}`;
}

function unmarkPickup(doc: Document, plan: PickupPlan): string {
    const content = Math.max(...plan.firsts.map(s => s.content));
    const pad = plan.firsts[0]!.expected - content;
    if (pad > 0) {
        for (const scan of plan.firsts) for (const run of scan.runs) padRun(doc, run, pad, scan.divisions, false);
    }
    if (plan.lasts) {
        const lastContent = Math.max(...plan.lasts.map(s => s.content));
        const lastPad = plan.lasts[0]!.expected - lastContent;
        if (lastPad > 0) {
            for (const scan of plan.lasts) for (const run of scan.runs) padRun(doc, run, lastPad, scan.divisions, true);
        }
    }
    for (const part of plan.parts) {
        const first = part.querySelector('measure');
        first?.removeAttribute('implicit');
        first?.setAttribute('number', '1');
        renumberMeasures(part);
    }
    return 'Auftakt aufgehoben';
}
